import { isWithinInterval } from 'date-fns/isWithinInterval';
import { PostHog } from 'posthog-js';

import { config } from '~/config';
import { apiFetch } from '~/utils/api-fetch/apiFetch';
import { create } from '~/utils/store/createStore';

export enum SupportedLanguages {
  EN = 'en',
  JA = 'ja',
  PT = 'pt',
  KO = 'ko',
}

interface FlagsLanguageEnabled {
  enabled: boolean;
}

export interface FlagsMaintenance {
  from: string;
  to: string;
}

export type FlagsLanguage = {
  [key in SupportedLanguages]?: FlagsLanguageEnabled;
};

export interface SystemFlags {
  maintenance?: FlagsMaintenance;
  languages?: FlagsLanguage;
  feature_web: FeatureFlag[];
}

const flagsEndpoint = `system/flags/v1`;

export type FeatureFlag =
  | 'sample'
  | 'earnings_insights'
  | 'alfred_everywhere_feature'
  | 'hide_alfred_download_button'
  | 'kg_geographic_exposure';

interface SystemFlagsStore {
  systemFlags: Omit<SystemFlags, 'feature_web'>;
  featureFlags: FeatureFlag[];
  posthogClient?: PostHog;
  initialise(posthogClient?: PostHog): Promise<void>;
  checkIsInMaintenance(): boolean;
  getFeatureFlagState: (flag: FeatureFlag) => boolean;
}
const configFlags = config.app.featureFlags;
export const useFlags = create<SystemFlagsStore>((set, get) => ({
  systemFlags: {},
  getFeatureFlagState: (flag: FeatureFlag) => get().featureFlags.includes(flag),
  featureFlags: configFlags,
  initialise: async posthogClient => {
    const systemFlags = await apiFetch<SystemFlags>(flagsEndpoint);

    if (posthogClient) {
      set({
        posthogClient,
      });

      posthogClient.onFeatureFlags(() => {
        const posthogFlags = posthogClient.featureFlags.getFlagsWithDetails();

        set(state => {
          const posthogFlagKeys = new Set(Object.keys(posthogFlags));
          const enabledFlags = Object.entries(posthogFlags)
            .filter(([_, details]) => details.enabled)
            .map(([key]) => key) as FeatureFlag[];

          const nonPosthogFlags = state.featureFlags.filter(
            flag => !posthogFlagKeys.has(flag)
          );

          return {
            featureFlags: [...nonPosthogFlags, ...enabledFlags],
          };
        });
      });
    }

    if (!systemFlags.error) {
      const { feature_web, ...rest } = systemFlags.data;
      set(state => ({
        systemFlags: rest,
        featureFlags: [...feature_web, ...state.featureFlags],
      }));
    }
  },
  checkIsInMaintenance: () => {
    const {
      systemFlags: { maintenance },
    } = get();

    if (maintenance) {
      const start = new Date(maintenance.from);
      const end = new Date(maintenance.to);
      return isWithinInterval(new Date(), {
        start,
        end,
      });
    }

    return false;
  },
}));
